import { useEffect, useState } from "react";

import { festivals, session, type FestivalSummary, type SessionUser } from "./api";
import { FestivalEditor } from "./FestivalEditor";
import { LineupEditor } from "./LineupEditor";
import { Login } from "./Login";
import { ContributionsEditor } from "./ContributionsEditor";
import { ReportsEditor } from "./ReportsEditor";
import { SourceEditor } from "./SourceEditor";
import { Invitations } from "./Invitations";
import { OrthophotoEditor } from "./OrthophotoEditor";
import { PlanEditor } from "./PlanEditor";
import { LauncherIconEditor } from "./LauncherIconEditor";
import { StaffEditor } from "./StaffEditor";
import { ThemeEditor } from "./ThemeEditor";
import { PoiEditor } from "./PoiEditor";

/**
 * Back-office organisateur.
 *
 * Une navigation volontairement plate : un festival, quelques onglets. Un
 * organisateur y vient rarement et sait ce qu'il vient faire — l'arborescence
 * serait un obstacle, pas une aide.
 */

type Tab =
  | "festival"
  | "pois"
  | "lineup"
  | "plan"
  | "contributions"
  | "reports"
  | "staff"
  | "theme";

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checked, setChecked] = useState(false);
  const [list, setList] = useState<FestivalSummary[]>([]);
  const [slug, setSlug] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("festival");

  useEffect(() => {
    session
      .me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setChecked(true));
  }, []);

  useEffect(() => {
    if (!user) return;
    festivals
      .list()
      .then((all) => {
        setList(all);
        setSlug((current) => current ?? all[0]?.slug ?? null);
      })
      .catch(() => setList([]));
  }, [user]);

  if (!checked) return null;
  if (!user) return <Login onSignedIn={setUser} />;

  return (
    <>
      <header className="app">
        <h1>Festipal — organisateur</h1>
        <div className="row">
          <span className="muted">{user.displayName}</span>
          <button
            onClick={() => {
              void session.logout().finally(() => setUser(null));
            }}
          >
            Se déconnecter
          </button>
        </div>
      </header>

      <main>
        <section className="card">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <label style={{ minWidth: 260 }}>
              Festival
              <select
                value={slug ?? ""}
                onChange={(e) => setSlug(e.target.value || null)}
              >
                {list.map((festival) => (
                  <option key={festival.slug} value={festival.slug}>
                    {festival.name["fr"] ?? festival.slug} (
                    {festival.status === "OFFICIAL" ? "officiel" : "communautaire"})
                  </option>
                ))}
              </select>
            </label>

            <div className="row">
              {(
                [
                  "festival",
                  "pois",
                  "lineup",
                  "plan",
                  "contributions",
                  "reports",
                  "staff",
                  "theme",
                ] as const
              ).map((name) => (
                <button
                  key={name}
                  className={tab === name ? "tab active" : "tab"}
                  onClick={() => setTab(name)}
                  aria-current={tab === name ? "page" : undefined}
                >
                  {name === "festival"
                    ? "Réglages"
                    : name === "pois"
                      ? "Lieux"
                      : name === "lineup"
                        ? "Programme"
                        : name === "plan"
                          ? "Plan"
                          : name === "contributions"
                            ? "Relevés"
                            : name === "reports"
                              ? "Signalements"
                              : name === "staff"
                                ? "Équipe"
                                : "Thème"}
                </button>
              ))}
            </div>
          </div>

          {list.length === 0 ? (
            <p className="muted">
              Aucun festival ne vous est rattaché. Revendiquez une page ou demandez à
              l'administrateur de vous y ajouter.
            </p>
          ) : null}
        </section>

        {slug && tab === "festival" ? <FestivalEditor slug={slug} /> : null}
        {slug && tab === "festival" ? <SourceEditor slug={slug} /> : null}
        {slug && tab === "pois" ? <PoiEditor slug={slug} /> : null}
        {slug && tab === "lineup" ? <LineupEditor slug={slug} /> : null}
        {slug && tab === "plan" ? <PlanEditor slug={slug} /> : null}
        {slug && tab === "plan" ? <OrthophotoEditor slug={slug} /> : null}
        {slug && tab === "contributions" ? <ContributionsEditor slug={slug} /> : null}
        {slug && tab === "reports" ? <ReportsEditor slug={slug} /> : null}
        {slug && tab === "staff" ? <StaffEditor slug={slug} /> : null}
        {slug && tab === "theme" ? <ThemeEditor slug={slug} /> : null}
        {slug && tab === "theme" ? <LauncherIconEditor slug={slug} /> : null}

        {/* Hors onglets : les invitations ne dépendent d'aucun festival. */}
        <Invitations />
      </main>
    </>
  );
}
